import * as THREE from "three";
import { gsap } from "gsap";
import { addToCart, showCartModal, hideCartModal, checkout } from "./cart.js";

/** Background image URL */
const BACKGROUND_IMAGE_URL = "/background.png";

/** Plane width */
const PLANE_WIDTH = 326;
/** Number of slides (will be updated based on data) */
let numSlides = 0;
/** Plane height */
const PLANE_HEIGHT = 256;
/** Current slide index */
let currentSlideIndex = 0;
/** Spacing between planes on the X-axis */
const SLIDE_SPACING_X = 80;

/**
 * Array to hold the slide objects
 * @type {SlideCard[]}
 */
const slides = [];
/**
 * Array to hold album data
 * @type {object[]}
 */
let imageData = [];

// Create 3D scene
const scene = new THREE.Scene();

// Create Camera
const camera = new THREE.PerspectiveCamera(30);
scene.add(camera);

// Create Renderer
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(devicePixelRatio);

renderer.setSize(window.innerWidth, window.innerHeight);

// Add Renderer element to the DOM
document.body.appendChild(renderer.domElement);

// Input element control (Slider)
const elementInput = document.querySelector("input#rangeSlider");
elementInput.addEventListener("input", onSliderChange); // Use the renamed function
// elementInput.focus(); // Focusing is fine, but commented out as it wasn't suitable for ICS MEDIA iframe embedding

// Mouse wheel support
window.addEventListener(
  "wheel",
  (event) => {
    // Adjust slider value based on wheel delta
    // The multiplier (0.0005) controls sensitivity, adjust as needed
    elementInput.valueAsNumber += event.deltaY * 0.0005;
    onSliderChange(); // Trigger slide movement
    event.preventDefault(); // Prevent default page scroll
  },
  { passive: false }
);

// --- Drag/Swipe Functionality ---
let isDragging = false;
let startX = 0;
let currentScrollLeft = 0; // Using this to simulate scroll position based on drag

// Add event listeners for mouse dragging
renderer.domElement.addEventListener("mousedown", onPointerDown);
renderer.domElement.addEventListener("mousemove", onPointerMove);
window.addEventListener("mouseup", onPointerUp); // Listen on window to catch mouseup outside the canvas

// Add event listeners for touch dragging
renderer.domElement.addEventListener("touchstart", onPointerDown, {
  passive: true,
}); // Use passive: true where possible
renderer.domElement.addEventListener("touchmove", onPointerMove, {
  passive: false,
}); // Need passive: false to preventDefault scroll
renderer.domElement.addEventListener("touchend", onPointerUp);
renderer.domElement.addEventListener("touchcancel", onPointerUp); // Handle cancellations

function onPointerDown(event) {
  isDragging = true;
  // Use clientX for mouse, touches[0].clientX for touch
  startX = event.clientX ?? event.touches[0].clientX;
  // Store the slider's current value at the start of the drag
  currentScrollLeft = elementInput.valueAsNumber;
  renderer.domElement.style.cursor = "grabbing"; // Change cursor
  // Prevent text selection during drag
  event.preventDefault();
}

function onPointerMove(event) {
  if (!isDragging) return;

  // Prevent default scrolling behavior during horizontal drag
  event.preventDefault();

  const currentX = event.clientX ?? event.touches[0].clientX;
  const deltaX = currentX - startX;

  // Adjust sensitivity: How much drag affects the slider value.
  // LARGER number = LESS sensitive drag (more friction).
  // Increased from window.innerWidth * 2 to * 4 for better mobile control
  const sensitivity = window.innerWidth * 4; // Adjust this factor as needed
  const scrollAmount = deltaX / sensitivity;

  // Update slider value based on drag, clamping between 0 and 1
  elementInput.valueAsNumber = Math.max(
    0,
    Math.min(1, currentScrollLeft - scrollAmount)
  );

  // Trigger the slider change handler to update the cover flow
  onSliderChange();
}

function onPointerUp() {
  if (!isDragging) return;
  isDragging = false;
  renderer.domElement.style.cursor = "grab"; // Restore cursor
}

// Initial cursor style
renderer.domElement.style.cursor = "grab";

// --- Click/Touch Event Handling ---
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
let clickStartTime = 0;
let clickStartPosition = { x: 0, y: 0 };
const TAP_MOVEMENT_THRESHOLD = 10; // pixels

/**
 * Handle click/tap on slides
 * @param {MouseEvent | TouchEvent} event
 */
function onSlideClick(event) {
  // Calculate mouse position in normalized device coordinates (-1 to +1)
  const clientX = event.clientX ?? event.changedTouches[0].clientX;
  const clientY = event.clientY ?? event.changedTouches[0].clientY;

  mouse.x = (clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(clientY / window.innerHeight) * 2 + 1;

  // Update the raycaster with camera and mouse position
  raycaster.setFromCamera(mouse, camera);

  // Calculate objects intersecting the picking ray
  const intersects = raycaster.intersectObjects(slides, true);

  if (intersects.length > 0) {
    // Get the first intersected object (closest to camera)
    const intersectedObject = intersects[0].object;

    // Find the parent SlideCard
    let slideCard = intersectedObject;
    while (slideCard && !(slideCard instanceof SlideCard)) {
      slideCard = slideCard.parent;
    }

    if (slideCard) {
      // Find the index of the clicked slide
      const slideIndex = slides.indexOf(slideCard);

      if (slideIndex !== -1) {
        // If clicking on the current centered slide, open modal
        if (slideIndex === currentSlideIndex) {
          handleSlideAction(slideIndex);
        } else {
          // Otherwise, move to the clicked slide
          moveSlide(slideIndex);
          // Update slider to reflect the change
          elementInput.valueAsNumber = slideIndex / (numSlides - 1);
        }
      }
    }
  }
}

/**
 * Custom action when clicking/tapping the centered slide
 * @param {number} slideIndex - Index of the clicked slide
 */
function handleSlideAction(slideIndex) {
  const album = imageData[slideIndex];
  console.log("Clicked on centered slide:", album.title);

  // Show the modal with album information
  showModal(album);
}

/**
 * Show modal with album details
 * @param {object} album - Album data object
 */
function showModal(image) {
  const modal = document.getElementById("albumModal");
  const modalImage = document.getElementById("modalImage");
  const modalPhotographer = document.getElementById("modalPhotographer");
  const modalPrice = document.getElementById("modalPrice");
  const modalDescription = document.getElementById("modalDescription");
  const modalAction = document.getElementById("modalAction");

  // Update modal content
  modalImage.src = image.image_url;
  modalImage.alt = image.title;
  modalPhotographer.textContent = image.photographer;
  modalDescription.textContent = `${image.description}`;
  modalPrice.textContent = `$150`;

  // Show the modal with animation
  modal.classList.add("show");

  // Prevent scrolling when modal is open
  document.body.style.overflow = "hidden";
}

/**
 * Hide the modal
 */
function hideModal() {
  const modal = document.getElementById("albumModal");
  modal.classList.remove("show");

  // Restore scrolling
  document.body.style.overflow = "";
}

// Setup modal event listeners
function setupModalListeners() {
  const closeBtn = document.getElementById("closeModal");
  const modal = document.getElementById("albumModal");
  const modalOverlay = modal?.querySelector(".modal-overlay");
  const modalAction = document.getElementById("modalAction");
  const checkoutBtn = document.getElementById("checkoutBtn");

  // Cart modal elements
  const closeCartBtn = document.getElementById("closeCartModal");
  const cartModal = document.getElementById("cartModal");
  const cartModalOverlay = cartModal?.querySelector(".modal-overlay");
  const cartCheckoutBtn = document.getElementById("cartCheckoutBtn");

  // Close album modal button
  if (closeBtn) {
    closeBtn.addEventListener("click", hideModal);
  }

  // Click outside to close album modal
  if (modalOverlay) {
    modalOverlay.addEventListener("click", hideModal);
  }

  // Escape key to close modals
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (modal?.classList.contains("show")) {
        hideModal();
      } else if (cartModal?.classList.contains("show")) {
        hideCartModal();
      }
    }
  });

  // Add to cart button in album modal
  if (modalAction) {
    modalAction.addEventListener("click", () => {
      const album = imageData[currentSlideIndex];
      addToCart(album);
      hideModal(); // Close album modal after adding to cart
    });
  }

  // Checkout button (opens cart modal)
  if (checkoutBtn) {
    checkoutBtn.addEventListener("click", () => {
      showCartModal();
    });
  }

  // Close cart modal button
  if (closeCartBtn) {
    closeCartBtn.addEventListener("click", hideCartModal);
  }

  // Click outside to close cart modal
  if (cartModalOverlay) {
    cartModalOverlay.addEventListener("click", hideCartModal);
  }

  // Cart checkout button (proceeds to Stripe)
  if (cartCheckoutBtn) {
    cartCheckoutBtn.addEventListener("click", async () => {
      await checkout();
    });
  }
}

// Initialize modal listeners when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setupModalListeners);
} else {
  setupModalListeners();
}

// Arrow key navigation for slides
document.addEventListener("keydown", (e) => {
  // Ignore if user is typing in an input field
  if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
    return;
  }

  if (e.key === "ArrowLeft") {
    // Move to previous slide
    const prevIndex = Math.max(0, currentSlideIndex - 1);
    if (prevIndex !== currentSlideIndex) {
      moveSlide(prevIndex);
      // Update slider to reflect the change
      elementInput.valueAsNumber = prevIndex / (numSlides - 1);
    }
    e.preventDefault();
  } else if (e.key === "ArrowRight") {
    // Move to next slide
    const nextIndex = Math.min(numSlides - 1, currentSlideIndex + 1);
    if (nextIndex !== currentSlideIndex) {
      moveSlide(nextIndex);
      // Update slider to reflect the change
      elementInput.valueAsNumber = nextIndex / (numSlides - 1);
    }
    e.preventDefault();
  }
});

/**
 * Handle pointer down for click detection
 * @param {MouseEvent | TouchEvent} event
 */
function onClickStart(event) {
  const clientX = event.clientX ?? event.touches[0].clientX;
  const clientY = event.clientY ?? event.touches[0].clientY;

  clickStartTime = Date.now();
  clickStartPosition = { x: clientX, y: clientY };
}

/**
 * Handle pointer up for click detection (touch)
 * @param {TouchEvent} event
 */
function onTouchEnd(event) {
  const clickEndTime = Date.now();
  const clickDuration = clickEndTime - clickStartTime;

  const clientX = event.changedTouches[0].clientX;
  const clientY = event.changedTouches[0].clientY;

  const deltaX = Math.abs(clientX - clickStartPosition.x);
  const deltaY = Math.abs(clientY - clickStartPosition.y);

  // Consider it a tap if:
  // - Duration is less than 300ms
  // - Movement is less than threshold (to distinguish from drag)
  if (
    clickDuration < 300 &&
    deltaX < TAP_MOVEMENT_THRESHOLD &&
    deltaY < TAP_MOVEMENT_THRESHOLD
  ) {
    // Single tap
    onSlideClick(event);
  }
}

/**
 * Handle mouse click (desktop)
 * @param {MouseEvent} event
 */
function onMouseClick(event) {
  const clickEndTime = Date.now();
  const clickDuration = clickEndTime - clickStartTime;

  const deltaX = Math.abs(event.clientX - clickStartPosition.x);
  const deltaY = Math.abs(event.clientY - clickStartPosition.y);

  // Only process if it wasn't a drag
  if (
    clickDuration < 300 &&
    deltaX < TAP_MOVEMENT_THRESHOLD &&
    deltaY < TAP_MOVEMENT_THRESHOLD
  ) {
    onSlideClick(event);
  }
}

// Add click/touch event listeners
renderer.domElement.addEventListener("mousedown", onClickStart);
renderer.domElement.addEventListener("click", onMouseClick);
renderer.domElement.addEventListener("touchend", onTouchEnd);
renderer.domElement.addEventListener("touchstart", onClickStart, {
  passive: true,
});

/**
 * Cover Flow Example (Three.js version)
 * @author IKEDA Yasunobu (Updated by Cline & Addy Osmani)
 */
async function init() {
  // Light
  const pointLight = new THREE.PointLight(0xffffff, 4, 1200);
  pointLight.position.set(0, 0, 600);
  scene.add(pointLight);

  // Load album data
  try {
    const response = await fetch("/images.json");
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    imageData = await response.json();
    numSlides = imageData.length; // Update slide count based on data
  } catch (error) {
    console.error("Failed to load album data:", error);
    // Optionally, display an error message to the user
    return; // Stop initialization if data loading fails
  }

  // Create Slides
  for (let i = 0; i < numSlides; i++) {
    // Create a slide card using album data
    const slide = new SlideCard(imageData[i]);

    // Add to the 3D scene
    scene.add(slide);

    // Store reference in the array
    slides[i] = slide;
  }

  // Camera position
  camera.position.z = 900;
  camera.lookAt(new THREE.Vector3(0, 0, 0));

  // Create Background
  const backgroundTexture = new THREE.TextureLoader().load(
    BACKGROUND_IMAGE_URL
  );
  const backgroundMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(3000, 1000),
    new THREE.MeshBasicMaterial({ map: backgroundTexture })
  );
  backgroundMesh.position.z = -500;
  scene.add(backgroundMesh);

  // Initial slide display (center)
  moveSlide(Math.floor(numSlides / 2)); // Use Math.floor for potentially odd numbers

  // Resize handling
  window.addEventListener("resize", onResize);
  onResize(); // Fit to window size initially

  // Start rendering loop
  tick();
}

/**
 * Event handler for slider input change.
 */
function onSliderChange() {
  const sliderValue = elementInput.valueAsNumber;
  // Calculate the target slide index from the slider value
  const nextIndex = Math.round(sliderValue * (numSlides - 1));
  // Move to the calculated slide
  moveSlide(nextIndex);
}

/**
 * Moves the slides to the target index.
 * @param {number} targetIndex - The index of the slide to center.
 */
function moveSlide(targetIndex) {
  // If the target index is the same as the current one, do nothing.
  if (currentSlideIndex === targetIndex) {
    return;
  }

  for (let i = 0; i < numSlides; i++) {
    // Initialize target position and rotation values
    let targetX = SLIDE_SPACING_X * (i - targetIndex); // Calculate base X position
    let targetZ = 0;
    let targetRotationY = 0;

    // Slides to the left of the target slide
    if (i < targetIndex) {
      targetX -= PLANE_WIDTH * 0.6; // Adjust X for spacing
      targetZ = PLANE_WIDTH; // Move back in Z
      targetRotationY = +45 * (Math.PI / 180); // Rotate
    }
    // Slides to the right of the target slide
    else if (i > targetIndex) {
      targetX += PLANE_WIDTH * 0.6; // Adjust X for spacing
      targetZ = PLANE_WIDTH; // Move back in Z
      targetRotationY = -45 * (Math.PI / 180); // Rotate
    }
    // The target slide itself
    else {
      targetX = 0; // Center X
      targetZ = 0; // Center Z
      targetRotationY = 0; // No rotation
    }

    // Get the reference to the current slide card
    const slide = slides[i];

    // Animate position using GSAP
    gsap.to(slide.position, {
      x: targetX,
      z: -1 * targetZ, // Invert Z for Three.js coordinate system
      duration: 1.8, // Animation duration in seconds
      ease: "expo.out", // Easing function
      overwrite: true, // Allow overwriting previous animations
    });

    // Animate rotation using GSAP
    gsap.to(slide.rotation, {
      y: targetRotationY,
      duration: 0.9, // Animation duration in seconds
      ease: "expo.out", // Easing function
      overwrite: true, // Allow overwriting previous animations
    });
  }

  currentSlideIndex = targetIndex; // Update the current index
}

/** Layout handling (also handles resize) */
function onResize() {
  renderer.setSize(window.innerWidth, window.innerHeight);
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
}

/** Frame update loop (Enter frame event) */
function tick() {
  // Render the scene
  renderer.render(scene, camera);
  // Request the next frame
  requestAnimationFrame(tick);
}

/**
 * Represents a single card/slide in the cover flow.
 */
class SlideCard extends THREE.Object3D {
  /**
   * @param {object} album - The album data object from albums.json.
   */
  constructor(album) {
    super();

    // Use CORS proxy if images are on different domains and cause issues
    // Example: const imageUrl = `https://cors-anywhere.herokuapp.com/${album.image_url}`;
    const imageUrl = album.image_url; // Assuming direct loading works

    const textureLoader = new THREE.TextureLoader();
    const texture = textureLoader.load(
      imageUrl,
      undefined, // onLoad callback (optional)
      undefined, // onProgress callback (optional)
      (error) => {
        // onError callback
        console.error(`Failed to load texture for ${album.title}:`, error);
        // Optionally load a placeholder texture
        // const placeholderTexture = textureLoader.load('./imgs/placeholder.jpg');
        // topMaterial.map = placeholderTexture;
        // reflectionMaterial.map = placeholderTexture;
        // topMaterial.needsUpdate = true;
        // reflectionMaterial.needsUpdate = true;
      }
    );

    // Top plane (main image)
    const topMaterial = new THREE.MeshLambertMaterial({
      map: texture,
    });

    const topPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(PLANE_WIDTH, PLANE_HEIGHT),
      topMaterial
    );
    this.add(topPlane);

    // Reflection plane
    const reflectionMaterial = new THREE.MeshLambertMaterial({
      map: texture,
      transparent: true,
      side: THREE.BackSide, // Render the back side for reflection
      opacity: 0.2,
    });

    const reflectionPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(PLANE_WIDTH, PLANE_HEIGHT),
      reflectionMaterial
    );
    // Flip the reflection vertically and position it below the main plane
    reflectionPlane.rotation.x = Math.PI; // Rotate 180 degrees around X-axis
    reflectionPlane.position.y = -PLANE_HEIGHT - 1; // Position below with a small gap
    this.add(reflectionPlane);
  }
}

// Execute the initialization code
init();
